import { cn } from '@quadralize/utils'
import * as LabelPrimitive from '@radix-ui/react-label'
import { DangerCircle } from '@solar-icons/react'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import * as React from 'react'

const labelVariants = cva(
  'text-sm font-medium leading-none text-text-primary peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
)

const Label = React.forwardRef<
  React.ComponentRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants> & { required?: boolean }
>(({ className, required, children, ...props }, ref) => (
  <LabelPrimitive.Root ref={ref} className={cn(labelVariants(), className)} {...props}>
    {children}
    {required && <span className="ml-1 text-primary">*</span>}
  </LabelPrimitive.Root>
))
Label.displayName = LabelPrimitive.Root.displayName

export { Label }

export interface LabelWrapperProps {
  label?: string
  id?: string
  required?: boolean
  error?: boolean
  children: React.ReactNode
  className?: string
  labelPosition?: 'top' | 'right'
  labelRight?: React.ReactNode
  helperText?: React.ReactNode
  helperId?: string
}

type AriaDescribedByProps = {
  'aria-describedby'?: string
}

export function LabelWrapper({
  label,
  id,
  required,
  error,
  children,
  className,
  labelPosition = 'top',
  labelRight,
  helperText,
  helperId,
}: LabelWrapperProps) {
  const generatedHelperId = React.useId()
  const resolvedHelperId = helperId || `helper-${generatedHelperId}`
  const labelClassName = cn(error && 'text-red-500')

  const helperContent = helperText && (
    <p
      id={resolvedHelperId}
      className={cn(
        'flex items-center gap-1 text-xs transition-colors',
        error ? 'text-red-500 font-medium' : 'text-text-tertiary',
      )}
    >
      {error && <DangerCircle className="h-3.5 w-3.5 shrink-0" />}
      {helperText}
    </p>
  )

  if (labelPosition === 'right') {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <div className="flex items-center gap-2">
          {children}
          {label && (
            <Label
              htmlFor={id}
              required={required}
              className={cn('cursor-pointer', labelClassName)}
            >
              {label}
            </Label>
          )}
        </div>
        {helperContent}
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-2 w-full', className)}>
      {label && (
        <div className="flex justify-between items-center">
          <Label htmlFor={id} required={required} className={labelClassName}>
            {label}
          </Label>
          {labelRight}
        </div>
      )}
      {React.Children.map(children, (child) => {
        if (React.isValidElement<AriaDescribedByProps>(child) && helperText) {
          return React.cloneElement(child, {
            'aria-describedby': cn(child.props['aria-describedby'], resolvedHelperId),
          })
        }
        return child
      })}
      {helperContent}
    </div>
  )
}
